import { useTheme } from "@mui/material";
import { composeProgress } from "apollo/utils";
import { useEnvContext } from "context/EnvContext";
import {
  APP_CLINIC,
  DOCUMENT_PRIVACY_POLICY,
  QUERY_PROGRESS_PENDING,
  TOKEN_INVALID_REASONS,
} from "core/consts";
import { getQueryBooleanVariable } from "core/model/utils/urls";
import { AppType, QueryProgressActive, TokenInvalidReason } from "core/types";
import InfoCaption from "ds_legacy/components/InfoCaption";
import LinkV2 from "ds_legacy/components/LinkV2";
import RSButton from "ds_legacy/components/RSButton";
import { RecareLogoOnly } from "ds_legacy/components/RecareLogo";
import { SpinnerPage } from "ds_legacy/components/Spinner";
import StrengthIndicator from "ds_legacy/components/StrengthIndicator";
import { TextInputField } from "ds_legacy/components/TextInputField";
import { dp, margin } from "ds_legacy/materials/metrics";
import { Body, Caption, Subheading } from "ds_legacy/materials/typography";
import { useLegalDocuments } from "dsl/atoms/LegalDocuments";
import { useOnEnter } from "dsl/atoms/Routes";
import { useOnboarding } from "dsl/ecosystems/CareproviderOnboarding";
import {
  Footer,
  FooterWrapper,
  Header,
  HeaderWrapper,
  LockCenter,
  OnboardingWidget,
  Overlay,
  StyledForm,
  Widget,
} from "dsl/ecosystems/CareproviderOnboardingLoginPage";
import {
  BottomWrapper,
  FlexContainer,
  InputFieldContainer,
} from "dsl/ecosystems/CareproviderOnboardingLoginPage/shared";
import usePasswordStrength from "dsl/hooks/usePasswordStrength";
import { CircleAlertIcon } from "lucide-react";
import { Dispatch, Fragment, SetStateAction, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslations } from "translations";
import Translations from "translations/types";
import { useChangePassword } from "./useChangePassword";
import { FormTypeValidation, useValidatePassword } from "./useValidatePassword";
import { useVerifyToken } from "./useVerifyToken";

export type PasswordResetProps = {
  changePassword: () => Promise<void>;
  isActivation?: boolean;
  loading: QueryProgressActive;
  password: string | undefined;
  passwordConfirmation: string | undefined;
  setPassword: Dispatch<SetStateAction<string | undefined>>;
  setPasswordConfirmation: Dispatch<SetStateAction<string | undefined>>;
  toAuth: () => void;
  tokenInvalidReason: TokenInvalidReason | undefined;
};

export const getChecks = (translations: Translations): FormTypeValidation[] => [
  {
    check: /^(?=.{8,})/,
    description: translations.login.passwordValidationLength,
    key: "length",
  },
  {
    check: /^(?=.*[0-9])/,
    description: translations.login.passwordValidationNumeric,
    key: "numeric",
  },
  {
    check: /^(?=.*[a-z])/,
    description: translations.login.passwordValidationLowerCase,
    key: "lower-case",
  },
  {
    check: /^(?=.*[A-Z])/,
    description: translations.login.passwordValidationUpperCase,
    key: "upper-case",
  },
];

export const getTokenErrorTitle = (
  translations: Translations,
  tokenInvalidReason: TokenInvalidReason | undefined,
): string => {
  switch (tokenInvalidReason) {
    case TOKEN_INVALID_REASONS.EXPIRED:
      return translations.login.linkExpired.linkExpiredTitle;
    case TOKEN_INVALID_REASONS.PASSWORD_CHANGED:
      return translations.login.passwordChanged;
    case TOKEN_INVALID_REASONS.INVALID:
      return translations.login.tokenInvalid;
    default:
      return translations.login.tokenInvalid;
  }
};

export function useGetTokenErrorText({
  app,
  isActivation,
  tokenInvalidReason,
}: {
  app: AppType;
  isActivation: boolean | undefined;
  tokenInvalidReason: TokenInvalidReason | undefined;
}) {
  const translations = useTranslations();

  if (tokenInvalidReason !== TOKEN_INVALID_REASONS.EXPIRED) return null;

  switch (app) {
    case APP_CLINIC:
      return isActivation
        ? translations.login.linkExpired.activationExpiredSender
        : translations.login.linkExpired.passwordLinkExpiredSender;

    default:
      return isActivation
        ? translations.login.linkExpired.activationExpiredReceiver
        : translations.login.linkExpired.passwordLinkExpiredReceiver;
  }
}

export function PasswordReset({
  changePassword,
  isActivation,
  loading,
  password,
  passwordConfirmation,
  setPassword,
  setPasswordConfirmation,
  validations,
}: {
  changePassword: () => Promise<void>;
  isActivation?: boolean;
  loading: QueryProgressActive;
  password: string | undefined;
  passwordConfirmation: string | undefined;
  setPassword: Dispatch<SetStateAction<string | undefined>>;
  setPasswordConfirmation: Dispatch<SetStateAction<string | undefined>>;
  validations: FormTypeValidation[];
}) {
  const translations = useTranslations();
  const { strength, track } = usePasswordStrength(password);

  if (loading === QUERY_PROGRESS_PENDING)
    return <SpinnerPage id="password-reset-page" />;

  return (
    <Fragment>
      <StyledForm
        noValidate
        style={{ height: "100%" }}
        onSubmit={(e) => {
          e.preventDefault();
          if (
            !password ||
            !passwordConfirmation ||
            password !== passwordConfirmation ||
            !!(validations && validations.length > 0)
          ) {
            return;
          }

          changePassword().then(() => track());
        }}
      >
        <InputFieldContainer>
          <TextInputField
            autoComplete="current-password"
            autoFocus
            elementName="password"
            label={translations.login.newPassword}
            required
            textInputType="password"
            value={password}
            onChange={setPassword}
          />
        </InputFieldContainer>
        <StrengthIndicator
          isHidden={!password}
          strength={strength}
          translations={translations}
        />
        <InputFieldContainer>
          <TextInputField
            autoComplete="current-password"
            elementName="password-confirmation"
            label={translations.login.newPasswordConfirmation}
            required
            textInputType="password"
            validateOverride={
              passwordConfirmation !== password
                ? translations.login.passwordsMustMatch
                : ""
            }
            value={passwordConfirmation}
            onChange={setPasswordConfirmation}
          />
        </InputFieldContainer>

        {validations && validations.length > 0 && (
          <Fragment>
            <Body>{translations.login.passwordRequirements}</Body>
            {validations.map((val) => (
              <Caption key={val.key}>{val.description}</Caption>
            ))}
          </Fragment>
        )}

        <BottomWrapper>
          <FlexContainer>
            <RSButton
              color="primary"
              id="change_password"
              loading="na"
              type="submit"
              variant="contained"
              onClick={() => {}}
            >
              {isActivation
                ? translations.actions.save
                : translations.login.changePassword}
            </RSButton>
          </FlexContainer>
          <FlexContainer>
            <InfoCaption margin={margin(3, 3, 3, 5)}>
              {translations.login.hintTemporaryLossOfEncryptedData}
            </InfoCaption>
          </FlexContainer>
        </BottomWrapper>
      </StyledForm>
    </Fragment>
  );
}

export function TokenErrorView({
  isActivation,
  toAuth,
  tokenInvalidReason,
}: Pick<PasswordResetProps, "isActivation" | "toAuth" | "tokenInvalidReason">) {
  const { app } = useEnvContext();
  const translations = useTranslations();

  const tokenErrorDescription = useGetTokenErrorText({
    app,
    isActivation,
    tokenInvalidReason,
  });

  return (
    <div className="mt-[100px] flex flex-1 flex-col items-center space-y-2 text-danger">
      <CircleAlertIcon size={40} />
      <Body as="h2" error margin={margin(1)}>
        {getTokenErrorTitle(translations, tokenInvalidReason)}
      </Body>
      {tokenErrorDescription && <Body as="p">{tokenErrorDescription}</Body>}
      <div className="absolute bottom-[72px] flex w-[220px] flex-col">
        <RSButton
          color="primary"
          id="login"
          loading="na"
          style={{ margin: 0, width: "100%" }}
          variant="contained"
          onClick={() => toAuth()}
        >
          {translations.login.login}
        </RSButton>
      </div>
    </div>
  );
}

export function PasswordResetView({
  changePassword,
  isActivation,
  loading,
  password,
  passwordConfirmation,
  setPassword,
  setPasswordConfirmation,
  toAuth,
  tokenInvalidReason,
}: PasswordResetProps) {
  const translations = useTranslations();
  const [validations, setValidations] = useState<FormTypeValidation[]>([]);

  useValidatePassword({ password, passwordConfirmation, setValidations });
  const { getDocumentUrl } = useLegalDocuments();
  const theme = useTheme();

  return (
    <>
      <Header>
        <HeaderWrapper>
          <Subheading light>
            {isActivation
              ? translations.login.newPassword.toUpperCase()
              : translations.login.changePassword.toUpperCase()}
          </Subheading>
          <RecareLogoOnly />
        </HeaderWrapper>
      </Header>
      {tokenInvalidReason === undefined ? (
        <PasswordReset
          changePassword={changePassword}
          isActivation={isActivation}
          loading={loading}
          password={password}
          passwordConfirmation={passwordConfirmation}
          setPassword={setPassword}
          setPasswordConfirmation={setPasswordConfirmation}
          validations={validations}
        />
      ) : (
        <TokenErrorView
          isActivation={isActivation}
          toAuth={toAuth}
          tokenInvalidReason={tokenInvalidReason}
        />
      )}

      <FooterWrapper>
        <Footer>
          <LinkV2
            color={theme.palette.common.black}
            href={getDocumentUrl(DOCUMENT_PRIVACY_POLICY)}
          >
            {translations.login.privacyPolicy}
          </LinkV2>
        </Footer>
      </FooterWrapper>
    </>
  );
}

export default function PasswordResetPage({
  authRoute,
}: {
  authRoute: string;
}) {
  useOnEnter();
  const navigate = useNavigate();
  const [password, setPassword] = useState<string>();
  const [passwordConfirmation, setPasswordConfirmation] = useState<string>();
  const { token } = useParams<{ token?: string }>(); // Getting the token for the Password Reset Page, if user didn't trigger it from Onboarding
  const { careproviderToken, passwordToken, resetPasswordPage } =
    useOnboarding();
  const resetToken = careproviderToken ? passwordToken : token;
  const isActivation = getQueryBooleanVariable(location.search, "activation");

  function toAuth() {
    return navigate(authRoute, { replace: true });
  }

  const [verifyTokenProgress, email, tokenInvalidReason] = useVerifyToken({
    isActivation,
    token: resetToken,
  });

  const [changePasswordProgress, changePassword] = useChangePassword({
    careproviderToken,
    email,
    isActivation,
    password,
    resetPasswordPage,
    resetToken,
  });

  function renderPasswordResetView() {
    return (
      <PasswordResetView
        changePassword={changePassword}
        isActivation={isActivation}
        loading={composeProgress(
          [changePasswordProgress, verifyTokenProgress],
          true,
        )}
        password={password}
        passwordConfirmation={passwordConfirmation}
        setPassword={setPassword}
        setPasswordConfirmation={setPasswordConfirmation}
        toAuth={toAuth}
        tokenInvalidReason={tokenInvalidReason}
      />
    );
  }

  if (careproviderToken) {
    return <OnboardingWidget>{renderPasswordResetView()}</OnboardingWidget>;
  }

  return (
    <div>
      <Overlay>
        <LockCenter>
          <Widget height={dp(530)}>{renderPasswordResetView()}</Widget>
        </LockCenter>
      </Overlay>
    </div>
  );
}
